import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Row,
  Col,
  Form,
  Button,
  Alert,
  Table,
  Spinner,
  Badge,
  Modal,
} from 'react-bootstrap';

import PrivateLayout from '../layouts/PrivateLayout';
import Panel from '../common/Panel';

const TOE_LABELS = {
  direct: 'Direct',
  secondary: 'Secondary',
  aggravation: 'Aggravation',
  presumptive: 'Presumptive',
  tera: 'TERA',
};

const TOE_REFRESH_STATUS_LABELS = {
  processing: 'Refreshing',
  completed: 'Ready',
  failed: 'Refresh Failed',
};

const TOE_REFRESH_STATUS_VARIANTS = {
  processing: 'warning',
  completed: 'success',
  failed: 'danger',
};

const parseSourceUrlsText = (value) => (
  (value || '')
    .split(/\r?\n/g)
    .map((line) => line.trim())
    .filter(Boolean)
);

const formatSourceUrlsText = (sourceUrls) => (
  Array.isArray(sourceUrls) ? sourceUrls.join('\n') : ''
);

const formatToeTypeLabel = (toeType) => {
  if (!toeType) return 'Pending';
  if (TOE_LABELS[toeType]) return TOE_LABELS[toeType];
  return toeType
    .split(/\s+/g)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const buildToeEditDraft = (toe = null) => ({
  toeId: toe?.toeId || '',
  toeType: toe?.toeType || '',
  title: toe?.title || '',
  shortDescription: toe?.shortDescription || '',
  sourceUrlsText: formatSourceUrlsText(toe?.sourceUrls || []),
  cfrReference: toe?.cfrReference || '',
  fullContent: toe?.fullContent || '',
  requirements: Array.isArray(toe?.requirements) ? toe.requirements : [],
  refreshStatus: toe?.refreshStatus || 'completed',
  refreshErrorMessage: toe?.refreshErrorMessage || null,
});

const ToeManager = () => {
  const [toes, setToes] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [submittingCreate, setSubmittingCreate] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createSourcesText, setCreateSourcesText] = useState('');

  const [showDetails, setShowDetails] = useState(false);
  const [activeToe, setActiveToe] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editDraft, setEditDraft] = useState(buildToeEditDraft());

  const hasRefreshingToes = useMemo(
    () => toes.some((toe) => toe.refreshStatus === 'processing'),
    [toes],
  );

  const loadToes = async () => {
    setLoadingList(true);
    try {
      const res = await axios.get('/casegpt/admin/toe?limit=200&includeContent=true');
      setToes(res.data?.toes || []);
    } catch (err) {
      setError('Failed to load TOE definitions');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadToes();
  }, []);

  useEffect(() => {
    if (!hasRefreshingToes) return undefined;

    const intervalId = window.setInterval(() => {
      loadToes();
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [hasRefreshingToes]);

  useEffect(() => {
    if (!activeToe?.toeId) return;
    const refreshedToe = toes.find((toe) => toe.toeId === activeToe.toeId);
    if (refreshedToe) {
      setActiveToe(refreshedToe);
    }
  }, [activeToe?.toeId, toes]);

  useEffect(() => {
    if (!showEditModal || !editDraft.toeId) return;
    const refreshedToe = toes.find((toe) => toe.toeId === editDraft.toeId);
    if (!refreshedToe) return;

    const nextDraft = buildToeEditDraft(refreshedToe);
    const didDraftChange =
      nextDraft.refreshStatus !== editDraft.refreshStatus ||
      nextDraft.cfrReference !== editDraft.cfrReference ||
      nextDraft.fullContent !== editDraft.fullContent ||
      nextDraft.title !== editDraft.title ||
      nextDraft.toeType !== editDraft.toeType ||
      nextDraft.shortDescription !== editDraft.shortDescription ||
      nextDraft.sourceUrlsText !== editDraft.sourceUrlsText ||
      JSON.stringify(nextDraft.requirements) !== JSON.stringify(editDraft.requirements);

    if (didDraftChange && editDraft.refreshStatus === 'processing') {
      setEditDraft(nextDraft);
    }
  }, [editDraft, showEditModal, toes]);

  const handleCreate = async () => {
    const sourceUrls = parseSourceUrlsText(createSourcesText);
    if (sourceUrls.length === 0) {
      setError('Add at least one source URL.');
      return;
    }

    setSubmittingCreate(true);
    setError(null);
    setSuccess(null);

    try {
      await axios.post('/casegpt/admin/toe', { sourceUrls });
      setSuccess('TOE creation started. Derived fields will populate after the refresh job finishes.');
      setCreateSourcesText('');
      setShowCreateModal(false);
      loadToes();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to create TOE');
    } finally {
      setSubmittingCreate(false);
    }
  };

  const handleDelete = async (toeId) => {
    if (!toeId) return;
    if (!window.confirm('Delete this TOE definition?')) return;

    try {
      await axios.delete(`/casegpt/admin/toe/${toeId}`);
      setSuccess('TOE deleted.');
      loadToes();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to delete TOE');
    }
  };

  const openDetails = (toe) => {
    setActiveToe(toe);
    setShowDetails(true);
  };

  const openEdit = (toe) => {
    setEditDraft(buildToeEditDraft(toe));
    setShowEditModal(true);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setActiveToe(null);
  };

  const closeEdit = () => {
    setShowEditModal(false);
    setEditDraft(buildToeEditDraft());
  };

  const handleEditFieldChange = (field, value) => {
    setEditDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!editDraft.toeId) {
      setError('No TOE selected for editing');
      return;
    }

    setSavingEdit(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        toeType: editDraft.toeType,
        title: editDraft.title,
        shortDescription: editDraft.shortDescription,
        sourceUrls: parseSourceUrlsText(editDraft.sourceUrlsText),
      };

      const response = await axios.put(`/casegpt/admin/toe/${editDraft.toeId}`, payload);
      const refreshed = response.data?.refreshed === true;

      setSuccess(
        refreshed
          ? 'TOE updated. Requirements and full content are refreshing in the background.'
          : 'TOE updated successfully.',
      );
      closeEdit();
      loadToes();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to update TOE');
    } finally {
      setSavingEdit(false);
    }
  };

  const renderRequirements = (requirements) => {
    if (!Array.isArray(requirements) || requirements.length === 0) {
      return <span className="text-muted">No requirements captured</span>;
    }
    return (
      <ul className="mb-0">
        {requirements.map((req, idx) => (
          <li key={`${req.label}-${idx}`}>
            <strong>{req.label}</strong>
            {req.description ? ` - ${req.description}` : ''}
          </li>
        ))}
      </ul>
    );
  };

  const renderSourceLinks = (sourceUrls) => {
    if (!Array.isArray(sourceUrls) || sourceUrls.length === 0) {
      return <span className="text-muted">No sources</span>;
    }

    return (
      <div className="d-flex flex-column gap-1">
        {sourceUrls.map((sourceUrl) => (
          <a key={sourceUrl} href={sourceUrl} target="_blank" rel="noreferrer" className="small">
            {sourceUrl}
          </a>
        ))}
      </div>
    );
  };

  const renderRefreshAlert = (toe) => {
    if (!toe || toe.refreshStatus !== 'processing') return null;

    return (
      <Alert variant="warning" className="mb-3">
        Requirements and full content are refreshing from the current source URLs. Existing derived
        content is shown below until the job finishes.
      </Alert>
    );
  };

  return (
    <PrivateLayout>
      <Panel className="pb-3 mb-3">
        <Row className="align-items-center">
          <Col>
            <h2 className="mb-2">Theory of Entitlement Manager</h2>
            <p className="text-muted mb-0">
              Create TOE definitions from source URLs and manage the derived library.
            </p>
          </Col>
          <Col xs="auto">
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              Add New Theory of Entitlement
            </Button>
          </Col>
        </Row>

        <hr />

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
            {success}
          </Alert>
        )}
        {hasRefreshingToes && (
          <Alert variant="info">
            One or more TOE definitions are refreshing. The list will update automatically.
          </Alert>
        )}

        <Row className="mb-2">
          <Col>
            <h5 className="mb-0">Existing TOE Definitions</h5>
          </Col>
        </Row>

        {loadingList ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : toes.length === 0 ? (
          <Alert variant="info">No TOE definitions found.</Alert>
        ) : (
          <Table responsive hover size="sm">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Reference</th>
                <th>Sources</th>
                <th>Status</th>
                <th>Updated</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {toes.map((toe) => (
                <tr key={toe.toeId}>
                  <td>
                    <strong>{toe.title || 'Generating TOE...'}</strong>
                    {toe.shortDescription && (
                      <div className="text-muted small">{toe.shortDescription}</div>
                    )}
                  </td>
                  <td>
                    <Badge bg="secondary">
                      {formatToeTypeLabel(toe.toeType)}
                    </Badge>
                  </td>
                  <td>{toe.cfrReference || <span className="text-muted">Pending</span>}</td>
                  <td style={{ minWidth: 260 }}>
                    {renderSourceLinks(toe.sourceUrls)}
                  </td>
                  <td>
                    <Badge bg={TOE_REFRESH_STATUS_VARIANTS[toe.refreshStatus] || 'secondary'}>
                      {TOE_REFRESH_STATUS_LABELS[toe.refreshStatus] || toe.refreshStatus || 'Unknown'}
                    </Badge>
                    {toe.refreshErrorMessage && (
                      <div className="text-danger small mt-1">{toe.refreshErrorMessage}</div>
                    )}
                  </td>
                  <td>{new Date(toe.updatedAt).toLocaleDateString()}</td>
                  <td className="text-end">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => openDetails(toe)}
                    >
                      View
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-2"
                      onClick={() => openEdit(toe)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(toe.toeId)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Theory of Entitlement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="secondary">
            Enter one source URL per line. The system will derive the title, type, reference,
            requirements, and full content from these sources.
          </Alert>
          <Form.Group>
            <Form.Label>Source URLs</Form.Label>
            <Form.Control
              as="textarea"
              rows={8}
              placeholder={[
                'https://www.ecfr.gov/current/title-38/section-3.310',
                'https://www.knowva.ebenefits.va.gov/system/templates/selfservice/...',
              ].join('\n')}
              value={createSourcesText}
              onChange={(event) => setCreateSourcesText(event.target.value)}
              disabled={submittingCreate}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={submittingCreate}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreate} disabled={submittingCreate}>
            {submittingCreate ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              'Create TOE'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDetails} onHide={closeDetails} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{activeToe?.title || 'TOE Details'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeToe && (
            <>
              {renderRefreshAlert(activeToe)}
              {activeToe.refreshErrorMessage && (
                <Alert variant="danger" className="mb-3">
                  {activeToe.refreshErrorMessage}
                </Alert>
              )}
              <div className="mb-2">
                <Badge bg="secondary" className="me-2">
                  {formatToeTypeLabel(activeToe.toeType)}
                </Badge>
                <span className="text-muted">{activeToe.cfrReference || 'Reference pending'}</span>
              </div>
              {activeToe.shortDescription && (
                <p className="text-muted">{activeToe.shortDescription}</p>
              )}
              <h6>Sources</h6>
              <div className="mb-3">
                {renderSourceLinks(activeToe.sourceUrls)}
              </div>
              <h6>Requirements</h6>
              {renderRequirements(activeToe.requirements)}
              <h6 className="mt-3">Full Content</h6>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {activeToe.fullContent || 'No content available.'}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showEditModal} onHide={closeEdit} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit TOE Definition</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editDraft.refreshStatus === 'processing' && (
            <Alert variant="warning">
              This TOE is currently refreshing. Editable fields are locked until the job finishes.
            </Alert>
          )}
          {editDraft.refreshErrorMessage && (
            <Alert variant="danger">{editDraft.refreshErrorMessage}</Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Type</Form.Label>
            <Form.Control
              type="text"
              list="toe-type-options"
              value={editDraft.toeType}
              onChange={(event) => handleEditFieldChange('toeType', event.target.value)}
              disabled={savingEdit || editDraft.refreshStatus === 'processing'}
            />
            <datalist id="toe-type-options">
              <option value="direct" />
              <option value="secondary" />
              <option value="aggravation" />
              <option value="presumptive" />
              <option value="tera" />
              <option value="southwest asia service" />
            </datalist>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={editDraft.title}
              onChange={(event) => handleEditFieldChange('title', event.target.value)}
              disabled={savingEdit || editDraft.refreshStatus === 'processing'}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Short Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={editDraft.shortDescription}
              onChange={(event) => handleEditFieldChange('shortDescription', event.target.value)}
              disabled={savingEdit || editDraft.refreshStatus === 'processing'}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Source URLs</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={editDraft.sourceUrlsText}
              onChange={(event) => handleEditFieldChange('sourceUrlsText', event.target.value)}
              disabled={savingEdit || editDraft.refreshStatus === 'processing'}
            />
            <Form.Text className="text-muted">Enter one source URL per line.</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Reference</Form.Label>
            <Form.Control
              type="text"
              value={editDraft.cfrReference || 'Pending'}
              disabled
              readOnly
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Requirements</Form.Label>
            <div className="border rounded p-3 bg-light">
              {renderRequirements(editDraft.requirements)}
            </div>
          </Form.Group>

          <Form.Group>
            <Form.Label>Full Content</Form.Label>
            <Form.Control
              as="textarea"
              rows={12}
              value={editDraft.fullContent || ''}
              disabled
              readOnly
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEdit} disabled={savingEdit}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveEdit}
            disabled={savingEdit || editDraft.refreshStatus === 'processing'}
          >
            {savingEdit ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </PrivateLayout>
  );
};

export default ToeManager;
